// React imports
import React, { useState } from "react";
import { MdEmail } from "react-icons/md";
import { FaXTwitter, FaYoutube } from "react-icons/fa6";
import { FaFacebookSquare } from "react-icons/fa";
import { FaLinkedin } from "react-icons/fa";
import { RiInstagramFill } from "react-icons/ri";
import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  AsYouType,
} from "libphonenumber-js";

// Assets
import phoneIcon from "../../assets/icons/phone-Icon.svg";

// Custom Hooks
import useForm from "../../hooks/useForm";
import { submitContactUs } from "../../store/thunks/contactUsThunk";
import { useThunk } from "../../hooks/useThunk";

// Constants
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";

// Components
import CustomButton from "../Form/CustomButton";
import Input from "../Form/Input";
import showToast from "../../utils/toastNotifications";

const ContactUsSection = () => {
  const [doSubmitContactUs, isSubmitting, contactUsError] =
    useThunk(submitContactUs);
  const [phoneNumberError, setPhoneNumberError] = useState("");

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    message: "",
  };

  const requiredFields = {
    firstName: true,
    lastName: true,
    email: true,
    phoneNumber: true,
    message: true,
  };

  const inputTypes = {
    firstName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    lastName: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    email: COMMON_FIELD_TYPES.EMAIL_FIELD,
    phoneNumber: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    message: COMMON_FIELD_TYPES.TEXT_FIELD,
  };

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    clearError,
    setTouched,
    setFormData,
  } = useForm(initialValues, requiredFields, inputTypes);

  const formatInternationalPhoneNumber = (input) => {
    // Remove any existing formatting first
    let cleanInput = input.replace(/[^\d+]/g, "");

    // If input doesn't start with +, and has digits, assume it needs country code
    if (cleanInput && !cleanInput.startsWith("+")) {
      cleanInput = "+" + cleanInput;
    }

    // Use AsYouType for real-time formatting
    const formatter = new AsYouType();
    const formatted = formatter.input(cleanInput);

    return formatted;
  };

  const validateInternationalPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim().length < 0) {
      return "Phone number is required";
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      return "Please enter a valid phone number";
    }

    try {
      const parsed = parsePhoneNumberFromString(phoneNumber);

      if (!parsed) {
        return "Please enter a valid phone number with country code";
      }

      if (!parsed.isValid()) {
        return "Please enter a valid phone number";
      }

      return null; // Valid
    } catch (error) {
      return "Please enter a valid international phone number";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phoneNumber: true,
      message: true,
    });

    setPhoneNumberError(validateInternationalPhoneNumber(formData.phoneNumber));

    if (!validateForm() || phoneNumberError) {
      return;
    } else {
      const fullName = `${formData.firstName} ${formData.lastName}`;

      const formDataWithFullName = {
        ...formData,
        name: fullName,
        phone: formData.phoneNumber,
      };

      const result = await doSubmitContactUs(formDataWithFullName);
      if (result?.success) {
        showToast(
          "success",
          "Inquiry submitted successfully!" || result?.message
        );
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          message: "",
        });
      } else {
        showToast("error", result?.error?.message);
      }
    }
  };

  return (
    <div className="md:w-11/12 xl:w-full h-full bg-[#FFFFFF] md:rounded-[10px] border-[1px] border-[#EEEEEE] shadow xl:max-h-[638px] mx-3 sm:mx-6 md:mx-0 md:my-0">
      <div className="flex flex-col md:flex-row md:justify-between bg-diagonal-sky">
        <div className="md:bg-diagonal-sky w-full relative p-[30px] md:w-1/2 md:max-w-[578px]">
          <div className="font-poppins font-semibold text-primary-text lg:text-4xl lg:leading-[130%] lg:tracking-[0%] lg:mb-9 xl:text-[46px] xl:leading-[130%] xl:tracking-[0%] xl:mb-9 xl:block md:text-4xl text-[1.50rem] xs:text-[1.80rem] leading-[130%] tracking-[0%] mb-9 px-2 md:p-0 flex flex-col items-center md:items-start">
            <div className="xl:w-[90px] xl:h-[9px] xl:ml-3 xl:mb-[23px] lg:w-[75px] lg:h-[8px] lg:ml-3 lg:mb-[20px] w-[75px] h-[8px] ml-3 mb-[25px] md:mb-[20px] bg-gradient-blue" />
            <span className="block text-center md:text-left">Contact Us</span>
          </div>
          <div className="flex flex-col items-start gap-5 md:gap-7">
            <p className="mb-5 text-[14px] font-semibold text-center font-poppins md:font-medium text-content-text md:p-0 md:text-base md:leading-normal lg:text-base xl:text-lg md:text-left sm:mb-0 ">
              Feel free to use the form or drop us email. Old-fashioned phone
              calls work too.
            </p>
            <h1 className="text-[12px] md:text-sm font-medium font-poppins text-content-text md:p-0 lg:text-base xl:text-lg text-start md:flex md:flex-col md:items-start md:gap-2 md:mt-2">
              <span className="block">Office Hours:</span>
              <div className="flex flex-col md:flex-row">
                <span className="block">Monday – Friday, </span>
                <span className="block">9 AM – 6 PM EAT</span>
              </div>
            </h1>

            <div className="flex items-center">
              <MdEmail size={24} className="text-[#2387C0]" />
              <a
                href="mailto:support@tenafix.co"
                className="w-full max-w-full ml-2 text-[12px] md:text-sm font-medium lg:text-base text-content-text cursor-pointer font-poppins"
              >
                support@tenafix.co
              </a>
            </div>
            <div className="flex items-start mb-5 sm:mb-0">
              <img src={phoneIcon} alt="Phone" className="w-6 h-6" />
              <div className="flex flex-row  w-full md:flex-col lg:flex-row ml-2 text-[12px] gap-y-0 md:gap-y-2 lg:gap-y-0 mt-0 md:mt-[3px] md:text-sm lg:text-base text-content-text font-semibold font-poppins leading-[30px] tracking-normal text-left">
                <a href="tel:+256773884476 ," className="mr-2">
                  +256 773 884476{" "}
                </a>
                <a href="tel:+256776003035">+256 776 003035</a>
              </div>
            </div>
            <div className="flex w-full mt-5 items-baseline gap-[30px] justify-center md:justify-start">
              {/* <FaXTwitter size={32} className="text-[#2387C0]" />
              <RiInstagramFill size={32} className="text-[#2387C0]" />*/}
              <a
                href="https://www.linkedin.com/company/tenafix/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedin size={32} className="text-[#2387C0]" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61578772218007"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFacebookSquare size={32} className="text-[#2387C0]" />
              </a>
            </div>
          </div>
        </div>
        <div className="xs:p-4 sm:p-5 md:p-0 md:w-1/2 ">
          <div className="flex flex-col w-full p-10 bg-background rounded-xl md:rounded-none md:h-full">
            <form className="w-full " onSubmit={handleSubmit}>
              <div className="mb-4">
                <label
                  className={`hidden md:block text-[14px] text-[#494949] font-medium mb-2 font-poppins after:content-['*'] after:text-red-500 after:ml-1`}
                >
                  Name
                </label>
                <label
                  className={`block md:hidden text-[14px] text-[#494949] font-medium mb-2 font-poppins after:content-['*'] after:text-red-500 after:ml-1`}
                >
                  First Name
                </label>
                <div className="flex flex-col w-full gap-4 lg:flex-row">
                  <Input
                    id="firstName"
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    error={touched.firstName && errors.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    onBlur={() => handleBlur("firstName", formData.firstName)}
                    // onFocus={() => clearError("firstName")}
                    placeholder="First Name"
                    className="h-[45px] font-poppins border border-[#81818180] "
                    outerContainerStyle="w-full lg:w-1/2 mb-0"
                    isRequired={true}
                  />
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    label="Last Name "
                    labelStyle="font-poppins lg:hidden"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    onBlur={() => handleBlur("lastName", formData.lastName)}
                    // onFocus={() => clearError("lastName")}
                    placeholder="Last Name"
                    error={touched.lastName && errors.lastName}
                    className="h-[45px] font-poppins border border-[#81818180]"
                    outerContainerStyle="w-full lg:w-1/2 mb-0"
                    isRequired={true}
                  />
                </div>
              </div>

              <div className="mb-4">
                <Input
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  onBlur={(e) => handleBlur("email", formData.email)}
                  // onFocus={() => clearError("email")}
                  placeholder="example@email.com"
                  error={touched.email && errors.email}
                  className="h-[45px] font-poppins border border-[#81818180]"
                  labelStyle="font-poppins "
                  isRequired={true}
                />
              </div>

              <div className="mb-4">
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  label="Phone"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    const masked = formatInternationalPhoneNumber(
                      e.target.value
                    );
                    handleInputChange("phoneNumber", masked);
                    setPhoneNumberError(
                      validateInternationalPhoneNumber(masked)
                    );
                  }}
                  onBlur={() => {
                    handleBlur("phoneNumber", formData.phoneNumber);
                    setPhoneNumberError(
                      validateInternationalPhoneNumber(formData.phoneNumber)
                    );
                  }}
                  // onFocus={() => clearError("phoneNumber")}
                  placeholder="xxx-xxx-xxxx"
                  error={touched.phoneNumber && phoneNumberError}
                  className="h-[45px] font-poppins border border-[#81818180]"
                  labelStyle="font-poppins "
                  isRequired={true}
                />
              </div>

              <div className="mb-6">
                <Input
                  id="message"
                  name="message"
                  label="Message"
                  type="textarea"
                  value={formData.message}
                  onChange={(e) => handleInputChange("message", e.target.value)}
                  onBlur={(e) => handleBlur("message", formData.message)}
                  // onFocus={() => clearError("message")}
                  placeholder="Type your message..."
                  error={errors.message && touched.message}
                  className="h-auto font-poppins resize-none border border-[#81818180]"
                  labelStyle="font-poppins"
                  inputStyle="h-auto min-h-[80px] lg:min-h-[120px]"
                  isRequired={true}
                />
              </div>

              <div className="flex justify-center">
                <CustomButton
                  buttonText="Submit"
                  type="submit"
                  className="rounded-[5px] font-alexandria font-semibold text-[16px] flex items-center justify-center border-none"
                  bgColor=""
                  width="w-[134px]"
                  height="h-[46px]"
                  hoverEffect="none"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  style={{
                    background:
                      "linear-gradient(97.65deg, #BE93F4 0.33%, #873FE2 93.35%)",
                    boxShadow: "0px 10px 15px 0px #01739933",
                    padding: "13px 37px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                  }}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsSection;
